import React, { useMemo, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Restaurant, MenuItem } from '../types';
import { productosService, Producto } from '../src/services/productosService';
import { gsap } from 'gsap';

interface RestaurantDetailProps {
  restaurant: Restaurant;
  menuItems: MenuItem[];
  onAddToCart: (item: MenuItem) => void;
  onBack: () => void;
}

const CATEGORY_ICONS: Record<string, string> = {
  'Del día': 'today',
  'Popular': 'local_fire_department',
  'Entradas': 'restaurant',
  'Guisados': 'soup_kitchen',
  'Salsas': 'water_drop',
  'Tortillas': 'flatware',
  'Pasteles': 'cake',
  'Pizzas': 'local_pizza',
  'Pastas': 'dinner_dining',
  'Postres': 'icecream',
  'Café': 'coffee',
  'Panadería': 'bakery_dining',
  'Snacks': 'lunch_dining',
};

const getFullImageUrl = (url: string | null): string | null => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const apiBase = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:7202/api').replace('/api', '');
  return `${apiBase}${url}`;
};

const mapProducto = (p: Producto): MenuItem => {
  const imageUrl = getFullImageUrl(p.imagenUrl);
  return {
    id: p.productoId.toString(),
    restaurantId: p.negocioId.toString(),
    name: p.nombre,
    description: p.descripcion ?? '',
    price: Number(p.precio),
    image: imageUrl ?? 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=600&q=80',
    category: p.esDelDia ? 'Del día' : 'Menú',
    vendorName: p.nombreNegocio
  };
};

const RestaurantDetail: React.FC<RestaurantDetailProps> = ({ restaurant, onAddToCart, onBack }) => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [addedItems, setAddedItems] = useState<Set<string>>(new Set());
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [modalClosing, setModalClosing] = useState(false);
  const [modalEntering, setModalEntering] = useState(false);
  const [filteredMenuItems, setFilteredMenuItems] = useState<MenuItem[]>([]);
  const [menuLoading, setMenuLoading] = useState(true);

  useEffect(() => {
    const negocioId = parseInt(restaurant.id, 10);
    if (isNaN(negocioId)) return;
    setMenuLoading(true);
    setActiveCategory('');
    productosService.getByNegocio(negocioId)
      .then((data) => setFilteredMenuItems(data.filter(p => p.estaActivo).map(mapProducto)))
      .catch(() => {})
      .finally(() => setMenuLoading(false));
  }, [restaurant.id]);

  const categories = useMemo(() => {
    const cats: string[] = [];
    filteredMenuItems.forEach(item => {
      if (!cats.includes(item.category)) cats.push(item.category);
    });
    if (cats.includes('Del día')) {
      return ['Del día', ...cats.filter(c => c !== 'Del día')];
    }
    return cats;
  }, [filteredMenuItems]);

  const groupedItems = useMemo(() => {
    const grouped: Record<string, MenuItem[]> = {};
    categories.forEach(cat => {
      grouped[cat] = filteredMenuItems.filter(item => item.category === cat);
    });
    return grouped;
  }, [filteredMenuItems, categories]);

  const containerRef = useRef<HTMLDivElement>(null);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0]);
    }
  }, [categories, activeCategory]);

  useEffect(() => {
    if (menuLoading || filteredMenuItems.length === 0) return;
    
    const ctx = gsap.context(() => {
      if (viewMode === 'grid') {
        const gridItems = containerRef.current?.querySelectorAll(".menu-item-card");
        if (gridItems && gridItems.length > 0) {
          gsap.fromTo(gridItems,
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration: 0.6, stagger: 0.08, ease: 'power2.out' }
          );
        }
      } else {
        const listItems = containerRef.current?.querySelectorAll(".menu-list-item");
        if (listItems && listItems.length > 0) {
          gsap.fromTo(listItems,
            { opacity: 0, x: -15 },
            { opacity: 1, x: 0, duration: 0.4, stagger: 0.04, ease: 'power2.out' }
          );
        }
        const sections = containerRef.current?.querySelectorAll(".category-section");
        if (sections && sections.length > 0) {
          gsap.fromTo(sections,
            { opacity: 0, y: 15 },
            { opacity: 1, y: 0, duration: 0.5, stagger: 0.1, ease: 'power2.out' }
          );
        }
      }
      const header = containerRef.current?.querySelector(".restaurant-header");
      if (header) {
        gsap.fromTo(header,
          { opacity: 0, y: -20 },
          { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }
        );
      }
    }, containerRef);
    return () => ctx.revert();
  }, [viewMode, menuLoading, filteredMenuItems.length]);

  const closeModal = (callback?: () => void) => {
    setModalClosing(true);
    setModalEntering(false);
    setTimeout(() => {
      setSelectedItem(null);
      setModalClosing(false);
      callback?.();
    }, 250);
  };

  useEffect(() => {
    if (selectedItem && !modalClosing) {
      setModalEntering(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setModalEntering(true);
        });
      });
    }
  }, [selectedItem]);

  const handleAddToCart = (item: MenuItem) => {
    onAddToCart(item);
    setAddedItems(prev => new Set(prev).add(item.id));
    setTimeout(() => {
      setAddedItems(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }, 1200);
  };

  const scrollToCategory = (cat: string) => {
    setActiveCategory(cat);
    const el = categoryRefs.current[cat];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div ref={containerRef} className="max-w-[1300px] mx-auto pt-2 pb-32 px-2 lg:px-2 mt-0">
      {/* Premium Header/Banner */}
      <section className="restaurant-header relative w-full mb-12">
        <div className="relative h-[300px] md:h-[400px] w-full rounded-[2rem] overflow-hidden shadow-premium">
          <div
            className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 hover:scale-105 "
            style={{ backgroundImage: `url("${restaurant.banner}")` }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/40 to-transparent"></div>

          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onBack();
            }}
            className="fixed top-16 left-1 z-[40] pointer-events-auto flex items-center gap-2 px-4 py-3 bg-white/10 backdrop-blur-md rounded-2xl text-white font-black text-xs uppercase tracking-widest border border-white/20 hover:bg-white/20 transition-all active:scale-95"
          >
            <span className="material-symbols-outlined text-sm">arrow_back</span>
            Volver
          </button>

          <div className="absolute bottom-10 left-10 md:left-16 right-10 md:right-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                {restaurant.category.split(' • ').map((cat, i) => (
                  <span key={i} className="px-3 py-1 rounded-full bg-primary/20 backdrop-blur-md border border-primary/30 text-[10px] font-black text-white uppercase tracking-widest">
                    {cat}
                  </span>
                ))}
              </div>
              <h1 className="text-4xl md:text-6xl font-black text-white tracking-tight mb-4">{restaurant.name}</h1>
              <div className="flex flex-wrap items-center gap-4">
                <div className=" sm:hidden flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-2xl border border-white/10 text-white opacity-10">
                  <span className="material-symbols-outlined text-accent filled"></span>
                  <span className="text-sm font-black">{restaurant.rating}</span>
                  <span className="text-[10px] font-bold opacity-60 ml-1"></span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-2xl border border-white/10 text-white">
                  <span className="material-symbols-outlined text-accent filled">star</span>
                  <span className="text-sm font-black">{restaurant.rating}</span>
                  <span className="text-[10px] font-bold opacity-60 ml-1">Rating</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-2xl border border-white/10 text-white">
                  <span className="material-symbols-outlined text-gray-300">schedule</span>
                  <span className="text-sm font-black">{restaurant.deliveryTime}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-2xl border border-white/10 text-white">
                  <span className="material-symbols-outlined text-gray-300">delivery_dining</span>
                  <span className="text-sm font-black">${restaurant.deliveryFee.toFixed(0)}</span>
                </div>
              </div>
            </div>
            <button className="h-14 px-8 rounded-2xl bg-white text-secondary font-black text-sm shadow-xl hover:bg-gray-100 transition-all active:scale-95">
              Más Información
            </button>
          </div>
        </div>
      </section>

      {/* View Toggle + Title */}
      <section className="flex flex-col gap-6 mb-8">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <h3 className="text-2xl font-black text-secondary dark:text-white mb-2">Menú</h3>
            <div className="h-1.5 w-12 bg-primary rounded-full"></div>
          </div>
          <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-white/5 rounded-2xl">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                viewMode === 'grid'
                  ? 'bg-white dark:bg-white/10 text-primary shadow-md'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
              }`}
            >
              <span className="material-symbols-outlined text-sm">grid_view</span>
              Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-white/10 text-primary shadow-md'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
              }`}
            >
              <span className="material-symbols-outlined text-sm">view_list</span>
              Lista
            </button>
          </div>
        </div>
      </section>

      {/* ========== LOADING ========== */}
      {menuLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-pulse">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-[2.2rem] bg-gray-200 dark:bg-white/5 p-5 flex flex-col gap-4">
              <div className="h-48 w-full rounded-[1.8rem] bg-gray-300 dark:bg-white/10" />
              <div className="flex flex-col gap-2 px-1">
                <div className="h-5 bg-gray-300 dark:bg-white/10 rounded-full w-3/4" />
                <div className="h-4 bg-gray-300 dark:bg-white/10 rounded-full w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ========== EMPTY STATE ========== */}
      {!menuLoading && filteredMenuItems.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <span className="material-symbols-outlined text-7xl text-gray-300 dark:text-gray-600 mb-6">restaurant_menu</span>
          <h3 className="text-2xl font-black text-secondary dark:text-white mb-2">Sin productos</h3>
          <p className="text-gray-400 font-medium">Este negocio aún no tiene productos disponibles</p>
        </div>
      )}

      {/* ========== VIEW 1: GRID ========== */}
      {!menuLoading && viewMode === 'grid' && (
        <section className="flex flex-col gap-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 dark:bg-slate-800/50 dark:rounded-[2.5rem]">
            {filteredMenuItems.map((item) => (
              <div
                key={item.id}
                onClick={() => setSelectedItem(item)}
                className="menu-item-card group glass-card rounded-[2.2rem] p-5 flex flex-col gap-5 shadow-premium-hover hover:shadow-premium-hover hover:-translate-y-2 transition-all duration-500 cursor-pointer active:scale-[0.98]"
              >
                <div className="relative h-48 w-full rounded-[1.8rem] overflow-hidden">
                  <img src={item.image} alt={item.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors"></div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCart(item);
                    }}
                    className={`absolute top-4 right-4 h-10 w-10 rounded-2xl backdrop-blur-md flex items-center justify-center shadow-lg transition-all ${
                    addedItems.has(item.id)
                      ? 'bg-green-500 text-white scale-110'
                      : 'bg-white/90 dark:bg-black/60 text-primary group-hover:bg-primary group-hover:text-white'
                  }`}
                  >
                    <span className="material-symbols-outlined font-black">
                      {addedItems.has(item.id) ? 'check' : 'add'}
                    </span>
                  </button>
                </div>
                <div className="px-1 flex flex-col flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-lg font-black text-secondary dark:text-white group-hover:text-primary transition-colors leading-tight">{item.name}</h4>
                    <span className="text-lg font-black text-primary">${item.price.toFixed(0)}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed">
                    {item.description}
                  </p>
                  <div className="mt-auto pt-6 flex items-center gap-2">
                    <span className="material-symbols-outlined text-xs text-secondary/40 dark:text-white/40">local_fire_department</span>
                    <span className="text-[10px] font-black uppercase tracking-widest text-secondary/40 dark:text-white/40">Popular</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ========== VIEW 2: LIST BY CATEGORY ========== */}
      {!menuLoading && viewMode === 'list' && (
        <section className="flex flex-col gap-0">
          {/* Sticky Category Navigation */}
          <div className="bg-gray-500/10  sticky top-16 z-20 -mx-2 px-2 py-2 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border-b border-gray-100 dark:border-white/5 mb-8 rounded-b-2xl">
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => scrollToCategory(cat)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest whitespace-nowrap transition-all shrink-0 ${
                    activeCategory === cat
                      ? 'bg-primary text-white shadow-lg shadow-primary/25'
                      : 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10'
                  }`}
                >
                  <span className="material-symbols-outlined text-sm">
                    {CATEGORY_ICONS[cat] || 'restaurant_menu'}
                  </span>
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Category Sections */}
          <div className="flex flex-col gap-10">
            {categories.map((cat) => (
              <div
                key={cat}
                ref={(el) => { categoryRefs.current[cat] = el; }}
                className="category-section scroll-mt-32"
              >
                {/* Category Header */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                    <span className="material-symbols-outlined">
                      {CATEGORY_ICONS[cat] || 'restaurant_menu'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xl font-black text-secondary dark:text-white uppercase tracking-wide">{cat}</h4>
                    <p className="text-xs font-bold text-gray-400 dark:text-gray-500">
                      {groupedItems[cat].length} {groupedItems[cat].length === 1 ? 'platillo' : 'platillos'}
                    </p>
                  </div>
                  <div className="h-px flex-1 bg-gradient-to-r from-gray-200 dark:from-white/10 to-transparent"></div>
                </div>

                {/* Items List */}
                <div className="glass-card rounded-[2rem] overflow-hidden divide-y divide-gray-100 dark:divide-white/5">
                  {groupedItems[cat].map((item) => (
                    <div
                      key={item.id}
                      className="menu-list-item group flex items-center gap-1 p-1.5 md:p-2 hover:bg-white/60 dark:hover:bg-white/[0.03] transition-all cursor-pointer"
                      onClick={() => setSelectedItem(item)}
                    >
                      {/* Item Image */}
                      <div
                        className="relative w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden shrink-0 shadow-md"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedItem(item);
                        }}
                      >
                        <img
                          src={item.image}
                          alt={item.name}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      </div>

                      {/* Item Info */}
                      <div className="flex-1 min-w-0">
                        <h5 className="text-base md:text-lg font-black text-secondary dark:text-white group-hover:text-primary transition-colors leading-tight mb-1 truncate">
                          {item.name}
                        </h5>
                        <p className="text-sm font-medium text-gray-400 dark:text-gray-500 line-clamp-1 leading-relaxed">
                          {item.description}
                        </p>
                      </div>

                      {/* Price + Add Button */}
                      <div className="flex items-center gap-4 shrink-0">
                        <span className="text-lg md:text-xl font-black text-primary">
                          ${item.price.toFixed(0)}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddToCart(item);
                          }}
                          className={`w-11 h-11 rounded-2xl flex items-center justify-center shadow-lg transition-all active:scale-90 ${
                            addedItems.has(item.id)
                              ? 'bg-green-500 text-white scale-110 rotate-0'
                              : 'bg-primary/10 text-primary hover:bg-primary hover:text-white hover:shadow-primary/25'
                          }`}
                        >
                          <span className="material-symbols-outlined text-sm font-black">
                            {addedItems.has(item.id) ? 'check' : 'add'}
                          </span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      {/* Item Detail Modal */}
      {selectedItem && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => closeModal()}
        >
          <div
            className={`relative w-full max-w-md max-h-[90vh] bg-white dark:bg-slate-900 rounded-[2rem] overflow-hidden shadow-2xl transform-gpu origin-center ${modalClosing ? 'animate-zoom-out' : modalEntering ? 'animate-zoom-in' : ''}`}
            style={!modalClosing && !modalEntering ? { opacity: 0, transform: 'scale(0.01)' } : undefined}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => closeModal()}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/30 backdrop-blur-md flex items-center justify-center text-white hover:bg-black/50 transition-all"
            >
              <span className="material-symbols-outlined text-sm">close</span>
            </button>

            <div className="max-h-[90vh] overflow-y-auto">
              {/* Image */}
              <div className="relative w-full h-64 md:h-80">
                <img
                  src={selectedItem.image}
                  alt={selectedItem.name}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>

              {/* Info */}
              <div className="p-6 flex flex-col gap-4">
                <div>
                  <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-[10px] font-black uppercase tracking-widest">
                    {selectedItem.category}
                  </span>
                </div>
                <h3 className="text-2xl font-black text-secondary dark:text-white leading-tight">
                  {selectedItem.name}
                </h3>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 leading-relaxed">
                  {selectedItem.description}
                </p>

                {/* Price + Add */}
                <div className="flex items-center justify-between pt-2">
                  <span className="text-3xl font-black text-primary">
                    ${selectedItem.price.toFixed(0)}
                  </span>
                  <button
                    onClick={() => {
                      const item = selectedItem;
                      closeModal(() => handleAddToCart(item));
                    }}
                    className="flex items-center gap-3 px-8 py-4 rounded-2xl bg-primary text-white font-black text-sm uppercase tracking-widest shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 active:scale-95 transition-all"
                  >
                    <span className="material-symbols-outlined text-sm">add_shopping_cart</span>
                    Agregar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default RestaurantDetail;
